import { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { withTranslation } from 'react-i18next'
import classNames from 'classnames'

import Typography from 'beam/dist/Typography'
import { CircularProgress } from 'beam/dist/Progress'
import { withStyles } from 'beam/dist/styles'

// Use this to prevent XSS, allowed tags are as follows: https://github.com/punkave/sanitize-html#what-are-the-default-options
import sanitize from 'sanitize-html'

import { CONTACT_TYPE } from '../MediaPlayer/mediaPlayer.constants'

import { authenticatedUserShape, withAuthenticatedUser, PERMISSIONS } from '../../../Permissions/'

const autoQaIdPrefix = 'App-Recordings-MediaPlayer-TextContent-'

const styles = theme => ({
  panelRoot: {
    wordBreak: 'break-word',
    '& pre': {
      whiteSpace: 'normal !important',
    },
  },
  center: {
    margin: 'auto auto',
  },
  subject: {
    marginBottom: theme.spacing(1),
  },
  content: {
    whiteSpace: 'normal',
  },
  phraseHit: {
    background: 'yellow',
  },
  lineBreak: {
    whiteSpace: 'pre-line',
  },
})

class TextContent extends Component {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    /** if the transcription data is still being loaded */
    loading: PropTypes.bool.isRequired,
    /** if loading the transcription data failed for some reason */
    error: PropTypes.bool.isRequired,
    /** type of Contact, should only be TEXT or EMAIL */
    contactType: PropTypes.string.isRequired,
    t: PropTypes.func.isRequired,
    /** Callback to load transcription data */
    requestTranscription: PropTypes.func.isRequired,
    /** id of call to load */
    ccrid: PropTypes.number,
    /** Result from the API */
    data: PropTypes.shape({
      ccrId: PropTypes.number,
      subject: PropTypes.string,
      emailBody: PropTypes.string,
      hits: PropTypes.array,
    }),
    requestText: PropTypes.func,
    authenticatedUser: authenticatedUserShape.isRequired,
    auditTextViewed: PropTypes.func.isRequired,
    completeReviewGoal: PropTypes.func.isRequired,
    goalId: PropTypes.number,
    hasReviewGoal: PropTypes.bool.isRequired,
  }

  componentDidMount() {
    const { ccrid, data } = this.props

    if (!data || data.ccrId !== ccrid) {
      this.requestText()
    }
  }

  preprocessTagsAndHits(text, hits, replacementMap) {
    text = sanitize(text)
    for (const key of Array.from(replacementMap.keys())) {
      text = text.replaceAll(key, replacementMap.get(key))
    }
    const baseRegexPattern = '(?<=^|\\s)(' + hits.map(hit => hit.phrase).join('|') + ')(?=[$\\s\\.,!?;:\'])'
    if (hits.length !== 0) {
      const oldregex = new RegExp(baseRegexPattern, 'gmi')
      const oldHits = text.match(oldregex).length
      const addRegexSuffix = oldHits !== hits.length
      let lastHit = 0
      hits.forEach(hit => {
        let escapedPhrase = hit.phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        if (escapedPhrase.includes('&')) {
          escapedPhrase = escapedPhrase.replace(/[&]/g, '&amp;')
          hit.length += 4
        }
        const regexPattern = addRegexSuffix ? '(?<=^|\\s)(' + escapedPhrase + ')(?=[$\\s\\.,!?;:])' : '(?<=^|\\s)(' + escapedPhrase + ')(?=[$\\s\\.,!?;:\'])'
        const regex = new RegExp(regexPattern, 'gmi')
        const j = text.toLowerCase().substring(lastHit).search(regex)
        hit.position = lastHit + j
        lastHit += j + hit.length
      })
    }
    //Text cannot be mutated here, so return it
    //The mutations to hits will remain, so no need to return
    // console.log(hits)
    return [text, hits]
  }

  highlightHits(retrievedText, retrievedHits) {
    retrievedHits = retrievedHits || []
    retrievedHits.sort((a, b) => a.position - b.position)

    const { classes } = this.props
    //Paragraphs auto close which causes formatting issues
    //replace them with lin breaks for more consistency
    const replacementMap = new Map()
    replacementMap.set('<p>', '<br>')
    replacementMap.set('</p>', '<br>')
    const processedValues = this.preprocessTagsAndHits(retrievedText, retrievedHits, replacementMap)
    const text = processedValues[0]
    const hits = processedValues[1]
    let prevIndex = 0
    // console.log(hits)
    return (
      <Fragment>
        {hits.map(hit => {
          const chunk = (
            <Fragment>
              <span className={classes.lineBreak}
                dangerouslySetInnerHTML={{
                  __html: (text.substring(prevIndex, hit.position)),
                }}
              />
              <span className={classNames(classes.phraseHit)}
                dangerouslySetInnerHTML={{ __html: text.substr(hit.position, hit.length) }}/>
            </Fragment>
          )
          prevIndex = hit.position + hit.length

          return chunk
        })}
        <span
          dangerouslySetInnerHTML={{
            __html: (text.substring(prevIndex)),
          }}
        />
      </Fragment>
    )
  }

  requestText() {
    const {
      requestText,
      ccrid,
      goalId,
      hasReviewGoal,
      auditTextViewed,
      completeReviewGoal,
      authenticatedUser: { user },
    } = this.props
    requestText(ccrid)
    auditTextViewed(ccrid)
    if (hasReviewGoal) {
      completeReviewGoal(ccrid, goalId, user.id)
    }
  }

  renderLoading() {
    return <CircularProgress />
  }

  renderError() {
    const { t } = this.props
    return (
      <div>
        <Typography color="secondary" align="center">
          {t('error')}
        </Typography>
        <Typography color="secondary" align="center">
          {t('textDataLoadError')}
        </Typography>
      </div>
    )
  }

  renderData() {
    const {
      classes,
      t,
      contactType,
      data: { subject, emailBody, hits },
      authenticatedUser: { hasPermission },
    } = this.props
    return (
      <section>
        {contactType === CONTACT_TYPE.EMAIL && (
          <Fragment>
            <div className={classes.subject}>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                {t('subject')}
              </Typography>
              <Typography auto-qa-id={`${autoQaIdPrefix}subject`} data-testid={`${autoQaIdPrefix}subject`} variant="body1">
                {subject}
              </Typography>
            </div>

            <div>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                {t('body')}
              </Typography>
            </div>
          </Fragment>
        )}

        { emailBody &&
          <Typography className={classes.content} auto-qa-id={`${autoQaIdPrefix}body`} data-testid={`${autoQaIdPrefix}body`} variant="body1">
            { hasPermission(PERMISSIONS.VIEW_TEXT_ANALYTICS) ?
              this.highlightHits(emailBody, hits) :

              <span className={classes.lineBreak} dangerouslySetInnerHTML={{
                __html: sanitize(emailBody),
              }} />
            }
          </Typography>
        }
      </section>
    )
  }

  render() {
    const { classes, loading, error } = this.props

    const noText = loading || error

    return (
      <div className={classNames(classes.panelRoot, noText && classes.center)}>
        {(loading && this.renderLoading()) || (error && this.renderError()) || this.renderData()}
      </div>
    )
  }
}

export { TextContent as UnstyledTextContent }
export default withStyles(styles)(withTranslation()(withAuthenticatedUser(TextContent)))
